import type OlMap from 'ol/Map'
import { layerInfoOf, type WmsTileLayer } from './layers'
import type { VectorModeController } from './vectorMode'

/**
 * Saved view state (PRD P1.4): last map center/zoom + visible layer names in
 * localStorage. STRICTLY non-sensitive data — never identity, tokens, or
 * anything from the session (preserves the P0.5 invariant: the viewer stores
 * no credential of any kind). Restored on boot and reconciled against the
 * currently-advertised layers; an explicit ?layer= deep link wins over it.
 */
const KEY = 'gv:view:v1'

export interface ViewState {
  center: [number, number]
  zoom: number
  layers: string[]
}

export function loadViewState(): ViewState | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ViewState>
    if (
      !Array.isArray(parsed.center) ||
      parsed.center.length !== 2 ||
      !parsed.center.every((n) => Number.isFinite(n)) ||
      typeof parsed.zoom !== 'number' ||
      !Array.isArray(parsed.layers) ||
      !parsed.layers.every((n) => typeof n === 'string')
    ) {
      return null
    }
    return parsed as ViewState
  } catch {
    return null
  }
}

/** Drops the saved view/layers, restoring first-visit behavior (default
 * extent fit, every advertised layer visible) on the next boot. */
export function clearViewState(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // storage blocked — nothing was saved anyway
  }
}

export function saveViewState(
  map: OlMap,
  wmsLayers: WmsTileLayer[],
  vector: VectorModeController | null,
): void {
  const view = map.getView()
  const center = view.getCenter()
  const zoom = view.getZoom()
  if (!center || zoom === undefined) return
  const rowVisible = (layer: WmsTileLayer): boolean =>
    vector ? vector.rowVisible(layer) : layer.getVisible()
  const state: ViewState = {
    center: [center[0], center[1]],
    zoom,
    layers: wmsLayers.filter(rowVisible).map((layer) => layerInfoOf(layer).name),
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(state))
  } catch {
    // storage full/blocked — saved state is best-effort only
  }
}
